import type React from "react";
import { Box, Tab, Tabs as MuiTabs, Container, Skeleton } from "@mui/material";
import { motion } from "framer-motion";
import type { CategoryResponse } from "../types/figma";
import { StarIcon } from "../../../components/icons";

interface CategoryTabsProps {
  categories: CategoryResponse[];
  selectedCategoryId: number | null;
  onCategoryChange: (categoryId: number | null) => void;
  isLoading?: boolean;
  isSticky: boolean;
}
export const FAVOURITE_TAB_ID = -1;
export const CategoryTabs: React.FC<CategoryTabsProps> = ({
  categories,
  selectedCategoryId,
  onCategoryChange,
  isLoading = false,
  isSticky,
}) => {
  const tabs = [
    {
      id: FAVOURITE_TAB_ID,
      label: "Favourite",
      icon: <StarIcon sx={{ fontSize: 18, color: "#FF9D00" }} />,
    },
    ...categories.map((cat) => ({
      id: cat.id,
      label: cat.name,
      icon: undefined,
    })),
  ];

  const selectedIndex = tabs.findIndex((tab) => tab.id === selectedCategoryId);
  const currentTabIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const enableScroll = tabs.length > 6;

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];
    onCategoryChange(selectedTab.id);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            backgroundColor: "#F0F4F8",
            borderRadius: 4,
            mt: 5,
            p: 2,
          }}
        >
          <Skeleton
            variant="rectangular"
            height={44}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          backgroundColor: "#F0F4F8",
          borderRadius: 4,
          mt: isSticky ? 0 : 5,
          zIndex: 100,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <MuiTabs
            value={currentTabIndex}
            onChange={handleChange}
            variant={enableScroll ? "scrollable" : "fullWidth"}
            scrollButtons={enableScroll ? true : false}
            sx={{
              px: { xs: 2, md: 6 },
              "& .MuiTabs-indicator": {
                backgroundColor: "#2196F3",
                height: 3,
              },
              "& .MuiTab-root": {
                minWidth: enableScroll ? 140 : "auto",
                flexShrink: 0,
              },
              "& .MuiTabs-scrollButtons": {
                display: enableScroll ? "flex" : "none",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id ?? "all"}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                sx={{
                  textTransform: "none",

                  fontWeight: 400,
                  fontSize: "14px",
                  minHeight: 60,
                  color: "#000000",
                  "&.Mui-selected": {
                    color: "#000",
                    fontWeight: 600,
                  },
                }}
              />
            ))}
          </MuiTabs>
        </motion.div>
      </Box>
    </Container>
  );
};
